import React, { useState, useRef, useEffect } from "react";
import "../styles/twoFactorInput.css"

interface TwoFactorInputProps {
  onComplete: (code: string) => Promise<void>;
}

const TwoFactorInput: React.FC<TwoFactorInputProps> = ({ onComplete }) => {
  const [codeDigits, setCodeDigits] = useState<string[]>(Array(6).fill(""));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(6).fill(null));

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleDigitInput = (value: string, index: number) => {
    if (!/^\d*$/.test(value)) return;
    const newCodeDigits = [...codeDigits];
    newCodeDigits[index] = value;
    setCodeDigits(newCodeDigits);

    if (value && index < 5 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace") {
      const newCodeDigits = [...codeDigits];
      if (codeDigits[index] === "" && index > 0 && inputRefs.current[index - 1]) {
        inputRefs.current[index - 1]?.focus();
      } else {
        newCodeDigits[index] = "";
        setCodeDigits(newCodeDigits);
      }
    }
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();

    if (pastedData.length === 6 && /^\d+$/.test(pastedData)) {
      const newCodeDigits = pastedData.split("");
      setCodeDigits(newCodeDigits);

      if (inputRefs.current[5]) {
        inputRefs.current[5]?.focus();
      }
    }
  };

  const handleSubmit = async () => {
    const verificationCode = codeDigits.join("");

    if (codeDigits.includes("") || isLoading) return;

    setIsLoading(true);

    try {
      await onComplete(verificationCode);
    } catch (error) {
      console.error("Verification failed", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="two-factor-input-container">
      <div className="two-factor-input-fields">
        {codeDigits.map((digit, index) => (
          <input
            key={index}
            type="text"
            maxLength={1}
            value={digit}
            onChange={e => handleDigitInput(e.target.value, index)}
            onKeyDown={e => handleKeyDown(e, index)}
            onPaste={handlePaste}
            ref={el => (inputRefs.current[index] = el)}
            className="two-factor-input"
          />
        ))}
      </div>
      <div className="text-center">
        <button
          type="submit"
          id="kt_sign_in_submit"
          className="btn btn-lg btn-primary w-100 mb-5 mt-10"
          disabled={isLoading || codeDigits.includes("")}
          onClick={handleSubmit}
        >
          {!isLoading && <span className="indicator-label">Submit</span>}
          {isLoading && (
            <span className="indicator-progress" style={{ display: "block" }}>
              Please wait...
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

export default TwoFactorInput;
